import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  createCalendarOverride,
  deleteCalendarOverride,
  listCalendarOverrides,
  updateCalendarOverride,
  type CalendarOverride,
  type CreateOverrideBody,
  type UpdateOverrideBody,
} from "@/api/calendar";

export const CALENDAR_OVERRIDES_KEY = (calendarId?: number) =>
  ["calendar-overrides", calendarId ?? "all"] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function useCalendarOverrides(calendarId: number | null) {
  return useQuery<CalendarOverride[], Error>({
    queryKey: CALENDAR_OVERRIDES_KEY(calendarId ?? undefined),
    queryFn: () => listCalendarOverrides(calendarId ? { calendar_id: calendarId } : {}),
    enabled: calendarId !== null,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useCreateCalendarOverride() {
  const qc = useQueryClient();
  return useMutation<CalendarOverride, Error, CreateOverrideBody>({
    mutationFn: createCalendarOverride,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CALENDAR_OVERRIDES_KEY(vars.calendar_id) });
    },
  });
}

export interface UpdateOverrideVars {
  id: number;
  calendar_id: number;
  body: UpdateOverrideBody;
}

export function useUpdateCalendarOverride() {
  const qc = useQueryClient();
  return useMutation<CalendarOverride, Error, UpdateOverrideVars>({
    mutationFn: ({ id, body }) => updateCalendarOverride(id, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CALENDAR_OVERRIDES_KEY(vars.calendar_id) });
    },
  });
}

export interface DeleteOverrideVars {
  id: number;
  calendar_id: number;
  edit_reason?: string;
}

export function useDeleteCalendarOverride() {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteOverrideVars>({
    mutationFn: ({ id, edit_reason }) => deleteCalendarOverride(id, edit_reason ?? ""),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: CALENDAR_OVERRIDES_KEY(vars.calendar_id) });
    },
  });
}
